/**
 * The Portfolio screen: the company in four numbers, what those numbers are made of, then every
 * program on one row. Pure: given the view the API returned, render it. Counts and ages only; the
 * only names are role holders.
 *
 * The totals used to be an alarm with no handle, so the middle of the page is now the triage list:
 * one line per cause, what it accounts for, how it is spread, and where to go. Causes are things
 * and may be ordered; people are counted and never ranked
 * (`docs/2026-09-20-program-structure.md`, "Roll-ups").
 *
 * There is no trend line. The record holds days, not months, and a line through five points claims
 * a confidence the data does not have, so the open-work tile is today's split with its window
 * stated instead, and each cause states its own spread in days.
 */
import type { PortfolioLine, PortfolioTotals, PortfolioView, TriageCause, TriageKind } from "@trellis/core";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { Tip } from "../components/tip.js";
import { PANEL_ANCHOR, PANEL_NAME } from "../components/anchor.js";
import { CountStrip, type CountCell } from "./program.js";
import { fmtDate, plural, portfolioTriage, TRIAGE_RULE } from "@trellis/core";

export type PortfolioScreenView = Omit<PortfolioView, "programs"> & { programs: (PortfolioLine & { canView?: boolean })[] };

export interface PortfolioScreenProps {
  view: PortfolioScreenView;
  programHref?: (programId: string) => string;
  onNavigate?: (href: string) => void;
}


/** A column heading that says what its column is, for a reader who did not write the model. */
function Th({ what, w, num, children }: { what: string; w?: string; num?: boolean; children: ComponentChildren }) {
  const style = [w ? `width: ${w}` : "", num ? "text-align: right" : ""].filter(Boolean).join("; ");
  return (
    <th {...(style ? { style } : {})} {...(num ? { class: "num" } : {})}>
      <Tip what={what}>{children}</Tip>
    </th>
  );
}

export function PortfolioScreen({ view, programHref, onNavigate }: PortfolioScreenProps) {
  const t = view.totals;
  const href = (p: PortfolioLine & { canView?: boolean }) => (p.canView === false ? undefined : programHref?.(p.programId));
  return (
    <main class="main portfolio">
      <div class="portfolio-head">
        <h1 class="h-lg">Portfolio</h1>
        <span class="label">{plural(t.programs, "program")} · {plural(t.people, "person", "people")} · {fmtDate(view.asOf)} · model v{view.modelVersion}</span>
      </div>

      <Counts t={t} />
      <p class="portfolio-line note dim">{view.line}</p>

      {view.programs.length > 0 && (
        <>
          <div class="portfolio-charts">
            <OpenWork view={view} />
            <MissedRules programs={view.programs} />
          </div>
          <Triage programs={view.programs} href={href} onNavigate={onNavigate} />
        </>
      )}

      <div class="panel table-panel">
        {view.programs.length === 0
          ? <div class="empty">No programs yet.</div>
          : <>
              <table class="portfolio-table desk-only">
                <thead>
                  <tr>
                    <Th w="230px" what="The contract, its delivery profile, how many people billed to it, and who leads it. The name opens the contract.">Program</Th>
                    <Th w="124px" what="One number out of a hundred, from the parts of the delivery model this contract can be measured against. It is a state, not a grade: a contract at 34 has things to fix, not a bad team.">Health</Th>
                    <Th w="150px" what="How many of the roles the model names have somebody in them. One person may hold more than one.">Roles</Th>
                    <Th what="What the customer has asked for that nobody has acknowledged, and how long the oldest has waited. The standard is a parameter of the model, per contract.">Customer asks</Th>
                    <Th w="112px" num what="Work somebody accepted and has not closed, and how much of it is past its date. Counts only; never a person.">Commitments</Th>
                    <Th w="132px" what="Recurring reports and meetings that were due and did not run, over the last thirty days.">Cadence</Th>
                    <Th w="112px" what="Modifications, option years and period dates Trellis read from the contract system, and how many nobody has checked against the signed document. The count is over the whole record, which is the list the contract page shows. Trellis reads these and models none of them.">Contract</Th>
                    <Th w="104px" what="Whether any project, space or channel is linked for this contract. Nothing is read from a system that is not linked here.">Linked</Th>
                    <Th w="104px" what="Which version of the delivery model this contract runs, and whether a model may read its sources at all.">Model</Th>
                  </tr>
                </thead>
                <tbody>{view.programs.map((p) => <Row key={p.programId} p={p} href={href(p)} onNavigate={onNavigate} />)}</tbody>
              </table>
              <div class="portfolio-cards phone-only">
                {view.programs.map((p) => <Card key={p.programId} p={p} href={href(p)} onNavigate={onNavigate} />)}
              </div>
            </>}
      </div>
    </main>
  );
}

/** The four numbers a portfolio lead opens the page for, in the strip the contract page uses. */
function Counts({ t }: { t: PortfolioTotals }) {
  const unfilled = t.rolesTotal - t.rolesFilled;
  const cells: CountCell[] = [
    { n: String(t.open), t: `open ${t.open === 1 ? "commitment" : "commitments"}`, what: "Work somebody accepted and has not closed, across every contract you can see." },
    { n: String(t.overdue), t: `past ${t.overdue === 1 ? "its" : "their"} date`, what: "Open commitments whose date has passed. They are counted in the open figure as well.", watch: t.overdue > 0 },
    // The cell counts what is unacknowledged and takes the rule when any of it is past the
    // standard: an ask still inside the standard is a queue, not something to answer for.
    { n: String(t.unacknowledged), t: `${t.unacknowledged === 1 ? "ask" : "asks"} unacknowledged`, what: "What the customer has asked for that nobody has acknowledged. How many and how old, never who.", watch: t.pastStandard > 0 },
    { n: String(unfilled), t: `${unfilled === 1 ? "role" : "roles"} unfilled`, what: "Roles the delivery model names with nobody in them, added up over every contract." },
  ];
  return <CountStrip cells={cells} />;
}

/**
 * Today's open work, split three ways rather than two. Most of what is past its date can be the
 * model's own cadence bookkeeping, and a sponsor reading "80 overdue" as eighty broken promises
 * reads it wrong, so the promised work is its own part of the bar.
 */
function OpenWork({ view }: { view: PortfolioScreenView }) {
  const t = view.totals;
  const onDate = Math.max(0, t.open - t.overdue);
  const lateWork = Math.min(t.overdue, t.overdueWork);
  const lateCadence = Math.max(0, t.overdue - lateWork);
  const window = Math.max(0, ...view.programs.map((p) => p.cadences.windowDays));
  const pct = (n: number) => `${(n / t.open) * 100}%`;
  return (
    <section class="chart">
      <div class="chart-head">
        <h2 class="chart-title">
          <Tip what="Confirmed work that is not closed, split by whether its date has passed and by what kind of work it is. A count taken now, not a rate.">Open work today</Tip>
        </h2>
        <span class="chart-total num">{t.open} open</span>
      </div>
      {t.open === 0
        ? <p class="chart-empty">Nothing open across the portfolio.</p>
        : (
          <>
            <div class="stack" role="img" aria-label={`${onDate} on date, ${lateWork} promised work past its date, ${lateCadence} cadence items past their date`}>
              {onDate > 0 && <span class="stack-part" style={`width:${pct(onDate)};background:var(--act)`} />}
              {lateWork > 0 && <span class="stack-part" style={`width:${pct(lateWork)};background:var(--bad)`} />}
              {lateCadence > 0 && <span class="stack-part" style={`width:${pct(lateCadence)};background:var(--warn)`} />}
            </div>
            <ul class="chart-key">
              <li><span class="swatch" style="background:var(--act)" /><Tip what="Open, and its date has not passed."><b class="num">{onDate}</b> on date</Tip></li>
              <li><span class="swatch" style="background:var(--bad)" /><Tip what="Work somebody promised, past its date. This is the number to read as late delivery."><b class="num">{lateWork}</b> promised, late</Tip></li>
              <li><span class="swatch" style="background:var(--warn)" /><Tip what="A cadence the model opened that nobody closed. One inside the window is the same event as a missed cadence, not a second one; an older one is overdue without being one of the misses."><b class="num">{lateCadence}</b> cadence, late</Tip></li>
            </ul>
          </>)}
      <p class="chart-note">As of {fmtDate(view.asOf)}{window ? `. Cadence counts cover ${window} days` : ""}. Trellis holds days of history, so there is no trend to draw yet; each cause below states its own spread instead.</p>
    </section>
  );
}

/**
 * Which cadence rules the misses are actually in. A hundred and fifty-six missed cadences reads as
 * a portfolio in collapse; the same number as "one rule, two days, thirty-two units" is a single
 * decision. Rules are ordered, which the model allows; units and people are not named here at all.
 */
function MissedRules({ programs }: { programs: PortfolioLine[] }) {
  // Keyed on the rule id, not its name: the model may name two rules the same, and merging
  // them would report one bar and one percentage for two different things.
  const byRule = new Map<string, { name: string; n: number }>();
  for (const p of programs) {
    for (const m of p.cadenceMisses) {
      const at = byRule.get(m.cadenceId) ?? { name: m.name, n: 0 };
      at.n += m.missed;
      byRule.set(m.cadenceId, at);
    }
  }
  const rules = [...byRule].map(([id, r]) => ({ id, ...r })).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  const total = rules.reduce((s, r) => s + r.n, 0);
  const top = rules[0];
  return (
    <section class="chart">
      <div class="chart-head">
        <h2 class="chart-title">
          <Tip what="Every missed cadence in the window, folded onto the rule that produced it. One rule missed on many units is one decision, not many.">Which rules the misses are in</Tip>
        </h2>
        <span class="chart-total num">{total} missed</span>
      </div>
      {rules.length === 0
        ? <p class="chart-empty">No cadence missed in the window.</p>
        : (
          <ul class="rule-bars">
            {rules.map((r) => (
              <li key={r.id}>
                <span class="rule-name">{r.name}</span>
                <span class="rule-bar" aria-hidden="true"><span style={`width:${(r.n / (top?.n || 1)) * 100}%`} /></span>
                <b class="num rule-n">{r.n}</b>
              </li>
            ))}
          </ul>)}
      <p class="chart-note">
        {top && total > 0
          ? `${top.name} is ${Math.round((top.n / total) * 100)}% of them. A miss and the open item it leaves behind are one event, counted once.`
          : "A miss and the open item it leaves behind are one event, counted once."}
      </p>
    </section>
  );
}

const KIND_LABEL: Record<TriageKind, string> = {
  ask: "Customer asks", contract: "Contract", role: "Accountability", overdue: "Promised work", cadence: "Cadence",
};

/**
 * The handle on the four numbers: every row behind them folded onto its cause, ordered by what
 * goes wrong if nobody acts, each one saying how much of which total it accounts for and opening
 * the contract where it can be settled.
 */
function Triage({ programs, href, onNavigate }: {
  programs: (PortfolioLine & { canView?: boolean })[];
  href: (p: PortfolioLine & { canView?: boolean }) => string | undefined;
  onNavigate: ((href: string) => void) | undefined;
}) {
  const [only, setOnly] = useState<TriageKind | undefined>(undefined);
  const { causes, caveat } = portfolioTriage({ programs });
  const byProgram = new Map(programs.map((p) => [p.programId, p]));
  const kinds = (["ask", "contract", "role", "overdue", "cadence"] as TriageKind[])
    .map((k) => ({ k, n: causes.filter((c) => c.kind === k).length }))
    .filter((x) => x.n > 0);
  const shown = only ? causes.filter((c) => c.kind === only) : causes;

  return (
    <section class="panel triage">
      <div class="triage-head">
        <h2 class="h-md">
          <Tip what={TRIAGE_RULE}>Where to start</Tip>
        </h2>
        <span class="label">{causes.length === 0 ? "Nothing waiting" : plural(causes.length, "thing")} to decide</span>
      </div>
      {caveat && <p class="triage-caveat">{caveat}</p>}
      {kinds.length > 1 && (
        <div class="filters">
          <span class="seg" role="group" aria-label="Filter by what kind of thing it is">
            <button type="button" aria-pressed={!only} onClick={() => setOnly(undefined)}>All {causes.length}</button>
            {kinds.map(({ k, n }) => (
              <button key={k} type="button" aria-pressed={only === k} onClick={() => setOnly(only === k ? undefined : k)}>{KIND_LABEL[k]} {n}</button>
            ))}
          </span>
        </div>
      )}
      {shown.length === 0
        ? <div class="empty">Nothing is waiting on you.</div>
        : (
          <ol class="triage-list">
            {shown.map((c) => {
              const p = byProgram.get(c.programId);
              const base = p ? href(p) : undefined;
              // The panel, not the top of the contract: the page is long and the reader
              // should not have to find the thing the line was about.
              return <Cause key={c.id} c={c} href={base ? `${base}#${PANEL_ANCHOR[c.kind]}` : undefined} onNavigate={onNavigate} />;
            })}
          </ol>)}
    </section>
  );
}

/**
 * The finding is the link. It used to be a line of its own reading "Open COMET · customer asks"
 * beside a heading that already said what was wrong, so the reader had to look past the sentence
 * they came for to find the thing to press. The destination and the landing behaviour are
 * unchanged; only what you click is. The accessible name carries the contract and the panel,
 * which the visible text no longer needs to repeat.
 */
function Cause({ c, href, onNavigate }: { c: TriageCause; href: string | undefined; onNavigate: ((href: string) => void) | undefined }) {
  const go = (e: MouseEvent) => { if (!href || !onNavigate || e.metaKey || e.ctrlKey || e.button !== 0) return; e.preventDefault(); onNavigate(href); };
  return (
    <li class="cause">
      <div class="cause-n"><b class="num">{c.accounts.n}</b></div>
      <div class="cause-body">
        <h3 class="cause-what">
          {href
            ? <a href={href} class={`cause-link ${c.tone}`} aria-label={`${c.what} — ${c.programName}, open its ${PANEL_NAME[c.kind]}`} onClick={go}>{c.what}</a>
            : <span class={c.tone}>{c.what}</span>}
          {" "}<span class={`pill ${c.tone}`}>{c.accounts.of}</span>
        </h3>
        <p class="cause-spread">{c.spread}</p>
        <p class="cause-risk">{c.risk}</p>
        <p class="cause-go">
          <span class="name">{c.programName}</span>
          {href ? <span class="cause-who"> · {PANEL_NAME[c.kind]}</span> : null}
          {c.who ? <span class="cause-who"> · {c.who} holds the role</span> : null}
        </p>
      </div>
    </li>
  );
}

/** The lead itself, not the deputy: "Deputy delivery lead" contains the words and is not it. */
const isDeliveryLead = (roleName: string) => /^delivery lead$/i.test(roleName);

function programName(p: PortfolioLine, href: string | undefined, onNavigate: ((href: string) => void) | undefined) {
  return href
    ? <a href={href} class="name" onClick={(e) => { if (!onNavigate || e.metaKey || e.ctrlKey || e.button !== 0) return; e.preventDefault(); onNavigate(href); }}>{p.name}</a>
    : <span class="name">{p.name}</span>;
}

function Row({ p, href, onNavigate }: { p: PortfolioLine & { canView?: boolean }; href: string | undefined; onNavigate: ((href: string) => void) | undefined }) {
  const f = facts(p);
  return (
    <tr>
      <td>{programName(p, href, onNavigate)}<div class="s">{f.who}{p.rosterStale ? <><br />{p.rosterStale} of {p.peopleCount} from a stale staffing window</> : null}</div></td>
      <td><HealthCell p={p} tipId={`health-${p.programId}-desk`} /></td>
      <td><span class={f.rolesTone}>{f.roles}</span>{p.roles.unfilled.length > 0 && <div class="s">{p.roles.unfilled.join(", ")} unfilled</div>}</td>
      <td>
        <span class={f.asksTone}>{f.asks}</span>
        <div class="s">{p.asks.pastStandard ? `${p.asks.pastStandard} past the standard · standard is ${p.asks.standard}` : `Standard is ${p.asks.standard}`}</div>
      </td>
      <td class="num">{p.open} open<div class="s"><span class={p.overdue ? "warn" : "ok"}>{p.overdue} overdue</span></div></td>
      <td><span class={f.cadenceTone}>{f.cadence}</span><div class="s">{plural(p.cadences.applicable, "cadence")} · {p.cadences.windowDays} days</div></td>
      <td><span class={f.contractTone}>{f.contract}</span>{p.contract.total > 0 && <div class="s">{f.contractSub}</div>}</td>
      <td>{f.linked}{p.linked.unreachable > 0 && <div class="s">{p.linked.unreachable} unreachable</div>}</td>
      <td>v{p.modelVersion}<div class="s">{f.model}</div></td>
    </tr>
  );
}

function Card({ p, href, onNavigate }: { p: PortfolioLine & { canView?: boolean }; href: string | undefined; onNavigate: ((href: string) => void) | undefined }) {
  const f = facts(p);
  return (
    <article class="portfolio-card">
      <div class="card-head">
        {programName(p, href, onNavigate)}
        <HealthCell p={p} tipId={`health-${p.programId}-phone`} />
      </div>
      <div class="s">{f.who}{p.rosterStale ? ` · ${p.rosterStale} of ${p.peopleCount} from a stale staffing window` : ""}</div>
      <dl class="card-facts">
        <div>
          <dt>Roles</dt>
          <dd class={f.rolesTone}>{f.roles}</dd>
          {p.roles.unfilled.length > 0 && <div class="s">{p.roles.unfilled.join(", ")} unfilled</div>}
        </div>
        <div>
          <dt>Customer asks</dt>
          <dd class={f.asksTone}>{f.asks}</dd>
          <div class="s">{p.asks.pastStandard ? `${p.asks.pastStandard} past the standard · standard is ${p.asks.standard}` : `Standard is ${p.asks.standard}`}</div>
        </div>
        <div>
          <dt>Commitments</dt>
          <dd>{p.open} open · <span class={p.overdue ? "warn" : "ok"}>{p.overdue} overdue</span></dd>
        </div>
        <div>
          <dt>Cadence</dt>
          <dd class={f.cadenceTone}>{f.cadence}</dd>
          <div class="s">{plural(p.cadences.applicable, "cadence")} · {p.cadences.windowDays} days</div>
        </div>
        <div>
          <dt>Contract</dt>
          <dd class={f.contractTone}>{f.contract}</dd>
          {p.contract.total > 0 && <div class="s">{f.contractSub}</div>}
        </div>
        <div>
          <dt>Linked</dt>
          <dd>{f.linked}</dd>
          {p.linked.unreachable > 0 && <div class="s">{p.linked.unreachable} unreachable</div>}
        </div>
        <div>
          <dt>Model</dt>
          <dd>v{p.modelVersion}</dd>
          <div class="s">{f.model}</div>
        </div>
      </dl>
    </article>
  );
}

function facts(p: PortfolioLine) {
  return {
    who: [p.profileName, plural(p.peopleCount, "person", "people"), p.lead].filter(Boolean).join(" · "),
    rolesTone: p.roles.filled === p.roles.total ? "ok" : p.roles.unfilled.some(isDeliveryLead) ? "bad" : "warn",
    roles: `${p.roles.filled} of ${p.roles.total} named`,
    asksTone: p.asks.pastStandard ? "bad" : p.asks.unacknowledged ? "warn" : "ok",
    asks: p.asks.unacknowledged ? `${p.asks.unacknowledged} unacknowledged, ${plural(p.asks.oldestDays, "day")}` : `0 unacknowledged · ${p.asks.open} open`,
    cadenceTone: p.cadences.missed === 0 ? "ok" : "warn",
    cadence: p.cadences.missed ? `${p.cadences.missed} missed` : "None missed",
    contractTone: p.contract.unverified ? "warn" : "ok",
    // The cell leads with what somebody has to act on. It used to lead with "2 current" over a
    // sub-line reading "2 unverified", two different twos a reader had to tell apart.
    // "None unverified", not "all checked": an event can also be corrected or superseded, and
    // neither of those is somebody having checked it against the signed document.
    contract: p.contract.total === 0 ? "Nothing read" : p.contract.unverified ? `${p.contract.unverified} of ${p.contract.total} unverified` : `None of ${p.contract.total} unverified`,
    contractSub: `${p.contract.current} effective in the last 90 days or still ahead`,
    linked: p.linked.count ? `${p.linked.count} linked` : "None",
    model: `Skills ${p.skillsEnabled ? "on" : "off"}${p.openGaps ? ` · ${plural(p.openGaps, "gap")} open` : ""}`,
  };
}

function HealthCell({ p, tipId }: { p: PortfolioLine; tipId: string }) {
  const tone = p.health.rating === "healthy" ? "ok" : p.health.rating === "watch" ? "warn" : p.health.rating === "attention" ? "bad" : "muted";
  const [head, ...rest] = p.healthTip.split("\n");
  const fill = p.health.rating === "healthy" ? "var(--good)" : p.health.rating === "watch" ? "var(--warn)" : p.health.rating === "attention" ? "var(--bad)" : "var(--off)";
  return (
    <div class="health-wrap">
      <button type="button" class={`health-btn ${tone}`} aria-describedby={tipId} aria-label={`Health: ${p.healthTip.replace(/\n/g, ". ")}`}>
        <span class="health-score">{p.health.score !== undefined ? <><b class="num">{p.health.score}</b> {p.health.label}</> : p.health.label}</span>
        {p.health.score !== undefined && <span class="bar" aria-hidden="true"><span style={{ width: `${p.health.score}%`, background: fill }} /></span>}
      </button>
      <div id={tipId} role="tooltip" class="health-tip"><div class="head">{head}</div>{rest.map((line, i) => <div key={i}>{line}</div>)}</div>
    </div>
  );
}
